<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue';
import UserInfo from '@/components/Shared/AppBar/UserInfo/UserInfo.vue';
import CompanyInfo from '@/components/Shared/AppBar/CompanyInfo/CompanyInfo.vue';
import Divider from '@/components/Shared/Divider/Divider.vue';
import MobileOrgSwitcher from './MobileOrgSwitcher.vue';
import { toSwitchableEmployers } from './switchableEmployers';
import MeService from '@/core/shared/me/me.service';
import AuthenticationService from '@/core/shared/authentication/authentication.service';
import ChevronRightSvg from '@/assets/svg/chevron-right.svg';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();

const meService = new MeService();
const authenticationService = new AuthenticationService();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

// The two morphing views of the same dropdown surface.
const view = ref<'account' | 'orgs'>('account');

// While the dropdown is open, lock background scroll so swiping the overlay does not
// scroll the page behind it (the Teleport drops the v-navigation-drawer's scroll lock).
// Closing also resets back to the account view so the next open starts fresh.
const BODY_SCROLL_LOCK_CLASS = 'overflow-hidden';
watch(isOpen, (open) => {
  if (open) {
    document.body.classList.add(BODY_SCROLL_LOCK_CLASS);
  } else {
    document.body.classList.remove(BODY_SCROLL_LOCK_CLASS);
    view.value = 'account';
  }
});

onUnmounted(() => {
  document.body.classList.remove(BODY_SCROLL_LOCK_CLASS);
});

// Only users with more than one switchable org get the switcher; everyone else
// sees a plain row.
const hasMultipleEmployers = computed(
  () => toSwitchableEmployers(meService.userProfile?.employerUserMaps).length > 1,
);

function close(): void {
  isOpen.value = false;
}

// Mirrors the desktop UserMenu/MobileDrawer logout: revoke locally, then hand off
// to UI2 which owns the session.
function onLogoutClick(): void {
  authenticationService.logout();
  meService.redirectToUI2('/employer/auth/logout');
}
</script>

<template>
  <Teleport to="body">
    <div v-if="isOpen" data-test="mobile-account-menu">
      <!-- Dim only below the navbar so the bar (and its close X) stay crisp, per Figma. -->
      <div
        class="fixed inset-x-0 bottom-0 top-10 z-40 bg-shade-860/40"
        data-test="account-menu-overlay"
        @click="close"
      />

      <div
        data-test="account-menu-panel"
        :class="[
          // top-10 is the 40px navbar; the 56px max-height cap is that 40px plus the
          // 16px bottom gap that matches the left-4/right-4 side gaps.
          'fixed left-4 right-4 top-10 z-50 max-h-[calc(100vh-56px)] rounded-lg bg-tint-0 shadow-menu',
          // In the orgs view the inner list owns scrolling; keep the panel fixed so the
          // Back/Search header stays put and we avoid nested scrollbars / scroll-chaining.
          view === 'orgs' ? 'overflow-hidden' : 'overflow-y-auto',
        ]"
      >
        <MobileOrgSwitcher v-if="view === 'orgs'" @back="view = 'account'" @close="close" />

        <div v-else class="flex flex-col py-2">
          <div v-if="meService.userProfile" class="px-4 py-2">
            <UserInfo :user="meService.userProfile" />
          </div>

          <Divider v-if="meService.userProfile && meService.employer" class="mx-3 my-1" />

          <button
            v-if="meService.employer && hasMultipleEmployers"
            type="button"
            class="flex w-full items-center justify-between px-4 py-2 text-left"
            data-test="open-org-switcher"
            @click="view = 'orgs'"
          >
            <CompanyInfo :employer="meService.employer" />
            <img :src="ChevronRightSvg" alt="" width="20" height="20" class="shrink-0" />
          </button>
          <div v-else-if="meService.employer" class="px-4 py-2">
            <CompanyInfo :employer="meService.employer" />
          </div>

          <Divider v-if="meService.employer" class="mx-3 my-1" />

          <button
            type="button"
            class="px-4 py-3 text-left text-sm text-shade-880"
            data-test="mobile-logout"
            @click="onLogoutClick"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
